import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Warning,
  WifiHigh,
  WifiSlash,
  Lightning,
  ShieldWarning,
  Trash,
  ArrowClockwise,
  Spinner,
  Funnel,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const severityConfig = {
  critical: { color: "bg-red-100 text-red-700 border-red-200", icon: ShieldWarning, labelKey: "common.critical", bg: "bg-red-50" },
  warning: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: Warning, labelKey: "common.warning", bg: "bg-amber-50" },
  info: { color: "bg-blue-100 text-blue-700 border-blue-200", icon: WifiHigh, labelKey: "common.info", bg: "bg-blue-50" },
};

const DeviceAlertsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const roleLabels = { admin: "Admin", customer: "Yonetici", staff: "Calisan" };

  const typeLabels = {
    bypass_confirmed: t("alert_types.bypass_confirmed"),
    bypass_warning: t("alert_types.bypass_warning"),
    offline: t("alert_types.offline"),
    online: t("alert_types.online"),
    switched_off: t("alert_types.switched_off"),
    stale: t("alert_types.stale"),
    leakage_current: t("alert_types.leakage_current"),
    fault: t("alert_types.fault"),
    error: t("alert_types.error"),
    low_balance: t("alert_types.low_balance"),
    zero_balance_cutoff: t("alert_types.zero_balance_cutoff"),
    switched_on: "Cihaz Acildi",
    prepayment_toggle: "On Odemeli Mod",
    bypass_detection_toggle: "Bypass Algilama",
    device_event: "Cihaz Olayi",
  };
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [runningMonitor, setRunningMonitor] = useState(false);
  const [runningBypass, setRunningBypass] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const isAdmin = user?.role === "admin";
  const isCustomer = user?.role === "customer";

  const fetchAlerts = useCallback(async () => {
    try {
      const endpoint = isAdmin ? "/api/admin/alerts" : "/api/customer/alerts";
      const params = filter !== "all" ? `?severity=${filter}` : "";
      const res = await axios.get(`${API_URL}${endpoint}${params}`);
      setAlerts(res.data);
    } catch {
      toast.error("Alarmlar yuklenemedi");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, filter]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleRunMonitor = async () => {
    setRunningMonitor(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/monitor/run`);
      const d = res.data;
      if (d.tuya_errors > 0 && d.tuya_errors >= d.devices_checked) {
        toast.error(`Tuya API baglantisi basarisiz! ${d.devices_checked} cihaz kontrol edilemedi. Tuya ayarlarinizi kontrol edin.`);
      } else if (d.alerts_created > 0) {
        toast.warning(`${d.devices_checked} cihaz kontrol edildi - ${d.alerts_created} yeni alarm olusturuldu (${d.offline_count} offline, ${d.switched_off_count} kapali)`);
      } else {
        toast.success(`${d.devices_checked} cihaz kontrol edildi - Tum cihazlar normal`);
      }
      fetchAlerts();
    } catch {
      toast.error("Kontrol basarisiz - sunucu hatasi");
    } finally {
      setRunningMonitor(false);
    }
  };

  const handleBypassCheck = async () => {
    setRunningBypass(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/bypass-check`);
      const data = res.data;
      if (data.bypass_alerts > 0) {
        toast.error(`BYPASS ALGILANDI! ${data.bypass_alerts} cihazda kacak tespit edildi`);
      } else if (data.devices_checked === 0) {
        toast.info("Bypass algilama aktif cihaz bulunamadi. Cihaz detayindan aktif edin.");
      } else {
        toast.success(`${data.devices_checked} cihaz kontrol edildi, kacak bulunamadi`);
      }
      fetchAlerts();
    } catch {
      toast.error("Bypass kontrolu basarisiz");
    } finally {
      setRunningBypass(false);
    }
  };

  const handleClearAlerts = async () => {
    try {
      await axios.delete(`${API_URL}/api/admin/alerts/clear`);
      toast.success("Tum alarmlar temizlendi");
      setAlerts([]);
    } catch {
      toast.error("Temizleme basarisiz");
    }
  };

  const criticalCount = alerts.filter(a => a.severity === "critical").length;
  const warningCount = alerts.filter(a => a.severity === "warning").length;
  const bypassCount = alerts.filter(a => a.type?.includes("bypass")).length;
  const offlineCount = alerts.filter(a => a.type === "offline" || a.type === "stale").length;
  const lowBalanceCount = alerts.filter(a => a.type === "low_balance" || a.type === "zero_balance_cutoff").length;
  const switchedOffCount = alerts.filter(a => a.type === "switched_off").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="device-alerts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("alerts_page.title")}</h1>
          <p className="text-slate-500 mt-1">
            {t("alerts_page.subtitle")}
          </p>
          <span className="inline-flex items-center text-xs text-slate-400 mt-1" data-testid="retention-info">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            Son 60 gün gösteriliyor
          </span>
        </div>
        <div className="flex items-center gap-2">
          {(isAdmin || isCustomer) && (
            <Button variant="default" className="bg-orange-600 hover:bg-orange-700 text-white" 
              onClick={handleBypassCheck} disabled={runningBypass}
              data-testid="bypass-check-btn">
              {runningBypass ? <Spinner size={16} className="animate-spin mr-2" /> : <Lightning size={16} className="mr-2" />}
              {t("alerts_page.bypass_check")}
            </Button>
          )}
          {isAdmin && (
            <>
              <Button variant="outline" onClick={handleRunMonitor} disabled={runningMonitor}
                data-testid="run-monitor-btn">
                {runningMonitor ? <Spinner size={16} className="animate-spin mr-2" /> : <ArrowClockwise size={16} className="mr-2" />}
                {t("alerts_page.general_check")}
              </Button>
              <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={handleClearAlerts}
                data-testid="clear-alerts-btn">
                <Trash size={16} className="mr-2" />
                {t("common.clear")}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4 text-center">
            <ShieldWarning size={24} className="mx-auto text-red-600 mb-1" />
            <p className="text-2xl font-bold text-red-700" data-testid="critical-count">{criticalCount}</p>
            <p className="text-xs text-red-600">{t("common.critical")}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 text-center">
            <Warning size={24} className="mx-auto text-amber-600 mb-1" />
            <p className="text-2xl font-bold text-amber-700" data-testid="warning-count">{warningCount}</p>
            <p className="text-xs text-amber-600">{t("common.warning")}</p>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-4 text-center">
            <Lightning size={24} className="mx-auto text-orange-600 mb-1" />
            <p className="text-2xl font-bold text-orange-700" data-testid="bypass-count">{bypassCount}</p>
            <p className="text-xs text-orange-600">{t("fixed_expenses.bypass")}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-slate-50/50">
          <CardContent className="p-4 text-center">
            <WifiSlash size={24} className="mx-auto text-slate-600 mb-1" />
            <p className="text-2xl font-bold text-slate-700" data-testid="offline-count">{offlineCount}</p>
            <p className="text-xs text-slate-600">Offline</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardContent className="p-4 text-center">
            <Warning size={24} className="mx-auto text-yellow-600 mb-1" />
            <p className="text-2xl font-bold text-yellow-700" data-testid="low-balance-count">{lowBalanceCount}</p>
            <p className="text-xs text-yellow-600">{t("alerts_page.low_balance")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2" data-testid="alert-filters">
        <Funnel size={16} className="text-slate-400" />
        {[
          { key: "all", label: t("common.all") },
          { key: "critical", label: t("common.critical") },
          { key: "warning", label: t("common.warning") },
          { key: "info", label: t("common.info") },
        ].map(f => (
          <Button key={f.key} size="sm"
            variant={filter === f.key ? "default" : "outline"}
            className={filter === f.key ? "bg-indigo-600 hover:bg-indigo-700" : ""}
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}>
            {f.label}
          </Button>
        ))}
      </div>

      {/* Alert List */}
      {alerts.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="p-12 text-center">
            <ShieldWarning size={48} className="mx-auto text-emerald-400 mb-3" />
            <h3 className="font-medium text-slate-700 mb-1">{t("alerts_page.no_alerts")}</h3>
            <p className="text-sm text-slate-500">{t("alerts_page.all_normal")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.slice((page - 1) * pageSize, page * pageSize).map((alert, idx) => {
            const config = severityConfig[alert.severity] || severityConfig.info;
            const IconComponent = config.icon;
            return (
              <Card key={alert.id || idx} className={`border ${config.bg} shadow-sm`} data-testid={`alert-item-${idx}`}>
                <CardContent className="p-4 flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <IconComponent size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={config.color}>{t(config.labelKey)}</Badge>
                      <Badge variant="outline" className="text-xs">
                        {typeLabels[alert.type] || alert.type}
                      </Badge>
                      {alert.device_name && (
                        <span className="text-xs text-slate-500 font-medium">{alert.device_name}</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700">{alert.message}</p>
                    {alert.performed_by_name && (
                      <p className="text-xs text-indigo-600 mt-1 font-medium" data-testid={`alert-performer-${idx}`}>
                        Islem yapan: {alert.performed_by_name} ({roleLabels[alert.performed_by_role] || alert.performed_by_role})
                      </p>
                    )}
                    {alert.timestamp && (
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(alert.timestamp).toLocaleString("tr-TR")}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      {alerts.length > pageSize && (
        <Pagination total={alerts.length} page={page} pageSize={pageSize}
          onPageChange={setPage} onPageSizeChange={setPageSize} />
      )}

      {/* Info */}
      <Card className="border-indigo-200 bg-indigo-50/50 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <p className="text-sm text-indigo-800">
            <strong>{t("alerts_page.how_it_works")}</strong> {t("alerts_page.how_it_works_desc")}
          </p>
          <p className="text-sm text-orange-800">
            <strong>{t("alerts_page.bypass_detection")}</strong> {t("alerts_page.bypass_detection_desc")}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeviceAlertsPage;
